/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { getApiUrl } from "@/utils/baseUrl";

export interface HubDevice {
  entityId: string;
  name: string;
  domain: string;
  state: string;
  on: boolean;
  available: boolean;
  /** toggle: flips on/off; press: fires once (scene, script, button); none: display only */
  action: "toggle" | "press" | "view" | "none";
  brightness?: number;
  battery?: number;
  /** virtual vacuum buttons: locate | home | stop */
  kind?: string;
  value?: string;
}

export interface HubPerson {
  name: string;
  home: boolean;
  state: string;
}

export interface HubState {
  configured: boolean;
  error?: string;
  devices: HubDevice[];
  people: HubPerson[];
  /** a picture of the vacuum was uploaded */
  vacuumPhoto?: boolean;
  /** offline servers that can be powered on with Wake-on-LAN */
  wake: { id: number; name: string }[];
}

export interface HAEntity {
  entityId: string;
  name: string;
  domain: string;
  state: string;
}

export async function getHub(): Promise<HubState> {
  const res = await fetch(getApiUrl("/ha/hub"), { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load the display devices");
  const data = (await res.json()) as HubState;
  return { ...data, devices: data.devices ?? [], people: data.people ?? [], wake: data.wake ?? [] };
}

export async function getHAEntities(): Promise<HAEntity[]> {
  const res = await fetch(getApiUrl("/ha/entities"), { credentials: "include" });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Failed to load Home Assistant entities");
  return data.entities ?? [];
}

export async function saveHubDevices(devices: { entityId: string; name?: string }[]): Promise<void> {
  const res = await fetch(getApiUrl("/ha/hub/devices"), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ devices }),
  });
  if (!res.ok) throw new Error("Failed to save the devices");
}

export async function pressHubDevice(entityId: string): Promise<void> {
  const res = await fetch(getApiUrl("/ha/hub/press"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ entityId }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || "Home Assistant did not accept the command");
  }
}

/** Current snapshot of a camera on the display, as an object URL (revoke it when done). */
export async function getHubCamera(entityId: string): Promise<string> {
  const res = await fetch(getApiUrl(`/ha/hub/camera/${encodeURIComponent(entityId)}`), { credentials: "include" });
  if (!res.ok) throw new Error("No picture");
  return URL.createObjectURL(await res.blob());
}

/** Power on an offline server with Wake-on-LAN. */
export async function wakeServer(agentId: number): Promise<void> {
  const res = await fetch(getApiUrl(`/monitor/agents/${agentId}/wake`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: "{}",
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error ?? "Wake failed");
}

export interface VacuumOption {
  value: string;
  label: string;
}

export interface VacuumControl {
  id: string;
  /** suction | water | mode */
  kind: string;
  name: string;
  options: VacuumOption[];
  current: string;
}

export interface VacuumInfo {
  entityId: string;
  name: string;
  state: string;
  battery?: number;
  fault?: string;
  controls: VacuumControl[];
  parts: { name: string; percent: number }[];
  photo?: boolean;
  canPause?: boolean;
}

export async function getVacuum(): Promise<VacuumInfo> {
  const res = await fetch(getApiUrl("/ha/vacuum"), { credentials: "include" });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Failed to load the vacuum");
  return { ...data, controls: data.controls ?? [], parts: data.parts ?? [] };
}

async function postVacuum(path: string, body: unknown): Promise<void> {
  const res = await fetch(getApiUrl(path), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error ?? "Failed");
}

export const setVacuumControl = (entityId: string, controlId: string, value: string) =>
  postVacuum("/ha/vacuum/set", { entityId, controlId, value });

export const vacuumAction = (entityId: string, action: "start" | "pause" | "stop" | "home" | "locate") =>
  postVacuum("/ha/vacuum/action", { entityId, action });

export interface WaterCandidate {
  id: string;
  name: string;
  domain: string;
}

export async function getWaterCandidates(): Promise<{ candidates: WaterCandidate[]; current: string }> {
  const res = await fetch(getApiUrl("/ha/vacuum/candidates"), { credentials: "include" });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Failed to load entities");
  return { candidates: data.candidates ?? [], current: data.current ?? "" };
}

export const setWaterSource = (entityId: string) => postVacuum("/ha/vacuum/water-source", { entityId });

/** The uploaded vacuum picture as an object URL (revoke it when done). */
export async function getVacuumPhoto(): Promise<string> {
  const res = await fetch(getApiUrl("/ha/vacuum/photo"), { credentials: "include" });
  if (!res.ok) throw new Error("No photo");
  return URL.createObjectURL(await res.blob());
}

/** Upload a picture for the vacuum's tile; no file clears it. */
export async function setVacuumPhoto(file: File | null): Promise<void> {
  const res = await fetch(getApiUrl("/ha/vacuum/photo"), { method: "PUT", credentials: "include", body: file ?? "" });
  if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error ?? "Upload failed");
}
